import { useCallback, useEffect, useMemo, useState } from 'react'
import Sheet from '../components/Sheet.jsx'
import AdaptiveListDetail from '../components/AdaptiveListDetail.jsx'
import Attachments from '../components/Attachments.jsx'
import Tags from '../components/Tags.jsx'
import Links from '../components/Links.jsx'
import { addBuild, getBuilds, updateBuild } from '../lib/db.js'
import { peso, parseAmount, shortDate, todayISO } from '../lib/format.js'
import { supabase } from '../lib/supabase.js'

/**
 * Things he raises money for until they are done.
 *
 * Almost every payment lands here on its own: ticking a linked payable paid in
 * Cashflow writes the build payment. This screen is where he watches progress
 * and, rarely, logs something by hand.
 *
 * Completed builds sort last, keep their history, and take nothing further.
 * That is the whole point of a build over a pot.
 */
export default function Builds({ toast, jump = null, onJumpHandled }) {
  const [builds, setBuilds] = useState(null)
  const [payments, setPayments] = useState([])
  const [error, setError] = useState(null)
  const [sheet, setSheet] = useState(null)
  const [selectedBuildId, setSelectedBuildId] = useState(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const { builds: b, payments: p } = await getBuilds()
      setBuilds(b)
      setPayments(p)
    } catch (e) {
      setError(e.message || String(e))
    }
  }, [])

  useEffect(() => { load() }, [load])

  /* Active first, then completed. Inside each group, biggest gap first —
     what still needs money should be what he sees. */
  const ordered = useMemo(() => {
    if (!builds) return []
    const raisedFor = (id) =>
      payments.filter((p) => p.build_id === id).reduce((s, p) => s + Number(p.amount || 0), 0)

    return builds
      .map((b) => {
        const raised = raisedFor(b.id)
        const target = Number(b.target || 0)
        return {
          ...b,
          raised,
          target,
          togo: Math.max(0, target - raised),
          pct: target > 0 ? Math.min(100, Math.round((raised / target) * 100)) : 0,
          done: b.status === 'Completed',
        }
      })
      .sort((a, b) => {
        if (a.done !== b.done) return a.done ? 1 : -1
        return b.togo - a.togo
      })
  }, [builds, payments])

  /* Arriving from a Log line: open the build it named once they have loaded. */
  useEffect(() => {
    if (!jump || jump.type !== 'build' || !builds) return
    if (builds.some((build) => build.id === jump.id)) setSelectedBuildId(jump.id)
    onJumpHandled?.()
  }, [jump, builds, onJumpHandled])

  if (error) {
    return (
      <div className="notice warn">
        <p style={{ margin: 0, fontSize: 14 }}>Could not read your builds. {error}</p>
      </div>
    )
  }
  if (!builds) return <p className="lbl" style={{ padding: '20px 2px' }}>Loading…</p>

  const selectedBuild = ordered.find((build) => build.id === selectedBuildId) ?? null
  const newBuildAction = (
    <button type="button" className="btn ghost split-new-action"
      onClick={() => setSheet({ kind: 'new' })}>
      New build
    </button>
  )

  return (
    <>
      <AdaptiveListDetail
        storageKey="mypersonalos.split.buildsWidth"
        listLabel="Builds"
        selectedId={selectedBuildId}
        emptyLabel="Select a build"
        toolbarAction={newBuildAction}
        list={(
          <>
            {ordered.length === 0 ? (
              <div className="empty">
                <h2 className="serif">No builds yet</h2>
                <p>
                  A build is something you raise money for until it is finished. Most get funded from
                  Cashflow — tick a linked payable paid and the payment lands here.
                </p>
              </div>
            ) : (
              <div className="card">
                <div className="rows">
                  {ordered.map((b) => (
                    <div className={'row build-row' + (selectedBuildId === b.id ? ' selected' : '')}
                      key={b.id} data-context-row="">
                      <button
                        type="button"
                        className="rowopen"
                        data-detail-id={b.id}
                        aria-current={selectedBuildId === b.id ? 'true' : undefined}
                        onClick={() => setSelectedBuildId(b.id)}
                      >
                        <span className="nm">
                          <b>{b.name}</b>
                          <span className="lbl">
                            {peso(b.raised)} of {peso(b.target)} · {b.pct}%
                          </span>
                        </span>
                        <span className="go" aria-hidden="true">›</span>
                      </button>
                      {b.done ? (
                        <button type="button" className="linkish build-status-action" data-context-action="" onClick={async () => {
                          try {
                            await updateBuild(b.id, { status: 'Active', completed_at: null })
                            load()
                            toast('Reopened')
                          } catch (e) {
                            toast(e.message || 'Could not reopen that.')
                          }
                        }}>
                          Reopen
                        </button>
                      ) : (
                        <button type="button" className="linkish build-status-action" data-context-action="" onClick={() => {
                          setSelectedBuildId(b.id)
                          setSheet({ kind: 'finish', build: b })
                        }}>
                          Mark done
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        detail={selectedBuild ? (
          <BuildDetailSheet
            key={selectedBuild.id}
            build={selectedBuild}
            payments={payments.filter((payment) => payment.build_id === selectedBuild.id)}
            onClose={() => setSelectedBuildId(null)}
            onLogPayment={() => setSheet({ kind: 'pay', build: selectedBuild })}
            toast={toast}
          />
        ) : null}
      />

      {sheet?.kind === 'new' ? (
        <NewBuildSheet
          onClose={() => setSheet(null)}
          onSaved={() => { setSheet(null); load() }}
          toast={toast}
        />
      ) : null}

      {sheet?.kind === 'pay' ? (
        <PaymentSheet
          build={sheet.build}
          onClose={() => setSheet(null)}
          onSaved={() => { setSheet(null); load() }}
          toast={toast}
        />
      ) : null}

      {sheet?.kind === 'finish' ? (
        <FinishSheet
          build={sheet.build}
          onClose={() => setSheet(null)}
          onDone={async () => {
            try {
              await updateBuild(sheet.build.id, { status: 'Completed', completed_at: todayISO() })
              setSheet(null)
              load()
              toast(sheet.build.name + ' finished')
            } catch (e) {
              toast(e.message || 'Could not finish that.')
            }
          }}
        />
      ) : null}
    </>
  )
}

function BuildDetailSheet({ build, payments, onClose, onLogPayment, toast }) {
  return (
    <Sheet title={build.name} onClose={onClose}>
      <div className="build-detail-summary">
        {build.done ? <span className="donetag">Done</span> : null}
        <div className="build-nums">
          <span className="raised num">{peso(build.raised)}</span>
          <span className="of">of {peso(build.target)}</span>
          <span className="pct num">{build.pct}%</span>
        </div>
        <div className="rail">
          <i style={{ width: build.pct + '%', background: build.done ? 'var(--pos)' : 'var(--swb)' }} />
        </div>
        <Tags target={{ type: 'build', id: build.id }} toast={toast} />
        <Links target={{ type: 'build', id: build.id }} toast={toast} compact />
        <Attachments target={{ type: 'build', id: build.id }} toast={toast} compact />
        <p className="lbl" style={{ margin: '9px 0 0' }}>
          {build.done
            ? payments.length + (payments.length === 1 ? ' payment' : ' payments')
            : build.togo > 0
              ? peso(build.togo) + ' to go'
              : 'Target reached'}
        </p>
      </div>

      {payments.length ? (
        <div className="card build-payment-history">
          <div className="rows">
            {payments.map((payment) => (
              <div className="row" key={payment.id}>
                <div className="nm">
                  <b style={{ fontWeight: 400 }}>{shortDate(payment.paid_on)}</b>
                  <span className="lbl">
                    {payment.note || (payment.source_payable_id ? 'From a payable' : 'Logged by hand')}
                  </span>
                </div>
                {payment.source_payable_id ? null : <span className="tag hand">HAND</span>}
                <span className="amt num">{peso(payment.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {!build.done ? (
        <button type="button" className="btn ghost" style={{ marginTop: 14 }} onClick={onLogPayment}>
          Log payment
        </button>
      ) : null}
    </Sheet>
  )
}

function NewBuildSheet({ onClose, onSaved, toast }) {
  const [name, setName] = useState('')
  const [target, setTarget] = useState('')
  const [busy, setBusy] = useState(false)

  return (
    <Sheet title="New build" onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          if (busy || !name.trim()) return
          setBusy(true)
          try {
            await addBuild({ name: name.trim(), target: parseAmount(target) })
            onSaved()
          } catch (err) {
            toast(err.message || 'Could not save that.')
          } finally {
            setBusy(false)
          }
        }}
      >
        <div className="field">
          <label className="lbl" htmlFor="b-name">What are you raising for</label>
          <input id="b-name" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="field">
          <label className="lbl" htmlFor="b-target">Target</label>
          <input
            id="b-target" inputMode="numeric" placeholder="0"
            value={target} onChange={(e) => setTarget(e.target.value)}
          />
        </div>
        <button className="btn" type="submit" data-sheet-primary disabled={busy || !name.trim()}>
          {busy ? 'Saving…' : 'Start it'}
        </button>
      </form>
    </Sheet>
  )
}

/**
 * Hand-logged payments exist for money that never went through a payout — a
 * bonus, a sale, cash from somewhere else. They are tagged so they never get
 * confused with payout-funded ones.
 */
function PaymentSheet({ build, onClose, onSaved, toast }) {
  const [amount, setAmount] = useState('')
  const [on, setOn] = useState(todayISO())
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  return (
    <Sheet title={'Log a payment to ' + build.name} onClose={onClose}>
      <form
        onSubmit={async (e) => {
          e.preventDefault()
          const amt = parseAmount(amount)
          if (busy || !amt) return
          setBusy(true)
          try {
            const { error } = await supabase.from('build_payments').insert({
              build_id: build.id,
              amount: amt,
              paid_on: on,
              note: note.trim() || null,
            })
            if (error) throw error
            await supabase.from('activity_log').insert({
              kind: 'build.payment',
              summary: peso(amt) + ' paid into ' + build.name,
              target_type: 'build',
              target_id: build.id,
            })
            onSaved()
          } catch (err) {
            toast(err.message || 'Could not save that.')
          } finally {
            setBusy(false)
          }
        }}
      >
        <div className="notice" style={{ marginTop: 0, marginBottom: 14 }}>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--ink-dim)' }}>
            For money that did not come through a payout. Anything funded from Cashflow logs
            itself when you tick the payable paid.
          </p>
        </div>
        <div className="field">
          <label className="lbl" htmlFor="p-amt">Amount</label>
          <input
            id="p-amt" inputMode="numeric" placeholder="0" required
            value={amount} onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <div className="field">
          <label className="lbl" htmlFor="p-on">Date</label>
          <input id="p-on" type="date" value={on} onChange={(e) => setOn(e.target.value)} required />
        </div>
        <div className="field">
          <label className="lbl" htmlFor="p-note">Where it came from</label>
          <input
            id="p-note" placeholder="Bonus, sale, savings…"
            value={note} onChange={(e) => setNote(e.target.value)}
          />
        </div>
        <button className="btn" type="submit" data-sheet-primary disabled={busy || !parseAmount(amount)}>
          {busy ? 'Saving…' : 'Log it'}
        </button>
      </form>
    </Sheet>
  )
}

/** Finishing is reversible, but it changes behaviour, so it gets a sentence. */
function FinishSheet({ build, onClose, onDone }) {
  return (
    <Sheet title={'Finish ' + build.name + '?'} onClose={onClose}>
      <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--ink-dim)' }}>
        It keeps every payment and drops to the bottom of the list. It stops accepting new ones,
        so ticking a payable linked to it will say so instead of logging quietly.
      </p>
      <p style={{ margin: '0 0 18px', fontSize: 13.5, color: 'var(--ink-dim)' }}>
        You can reopen it any time.
      </p>
      <div className="sheet-actions">
        <button type="button" className="btn ghost" onClick={onClose}>Not yet</button>
        <button type="button" className="btn" onClick={onDone}>Mark done</button>
      </div>
    </Sheet>
  )
}
